/* 
********************************************************
Data and photo creation and storage functions 
********************************************************
*/

//@ts-nocheck
// Toggle preceding from @ts-nocheck to @ts-check to enable TypeScript checking

import { consoleMsg } from "./utility";
import { mkAbs } from "./page-init";
import { PhotoManager } from "../classes/photo-manager";

// prettier-ignore
export { 
   isLocalStorageAvailable, 
   storeTableData,
   clearStorage,
   clearStorageOnStartup,
   buildDamageIssues,
   restorePhotoData,
};

// Make sure our app references are using the global app object associated with window
const app = window.app;

function isLocalStorageAvailable() {
	// Example provided in API book does not work.  This is the recommended approach from
	// MDN Developer Network
	try {
		var x = "__storage_test__";
		window.localStorage.setItem(x, x);
		window.localStorage.removeItem(x);
		return true;
	} catch (e) {
		return false;
	}
}

async function storeTableData(dataObject) {
	const storageKey = app.state.storageKey;
	const storageObject = {};
	for (const [key, value] of Object.entries(dataObject)) {
		storageObject[key] = value;
	}
	await store.set(`${storageKey}_vehicle-data`, storageObject);
	// Adding Delivery Notes storage
	await store.set(`${storageKey}_delivery-notes`, app.data.deliveryNotes);
}

async function clearStorageOnStartup(isLive) {
	if (!isLive) return;

	// Prevent re-clearing on refresh in the same tab
	if (sessionStorage.getItem("liveInitDone") === "1") return;

	const currentActivityId = new URLSearchParams(window.location.search).get('activity_id') || 'dev';

	// Crash recovery: submission was interrupted mid-process
	const submissionInProgress = localStorage.getItem("submission-in-progress");
	if (submissionInProgress === currentActivityId) {
		sessionStorage.setItem("liveInitDone", "1");
		const recoveryAlert = document.createElement("ion-alert");
		recoveryAlert.header = "Submission Interrupted";
		recoveryAlert.message = "Your previous submission was interrupted. Your inspection data has been preserved — return to the Signature page to retry.";
		recoveryAlert.buttons = [{ text: "OK", role: "confirm" }];
		document.body.appendChild(recoveryAlert);
		await recoveryAlert.componentOnReady();
		await recoveryAlert.present();
		await new Promise(resolve =>
			recoveryAlert.addEventListener("ionAlertDidDismiss", resolve, { once: true })
		);
		recoveryAlert.remove();
		return;
	}

	// Crash recovery: tab was killed mid-inspection (e.g., during photo capture)
	const activeInspection = localStorage.getItem("active-inspection");
	if (activeInspection === currentActivityId) {
		sessionStorage.setItem("liveInitDone", "1");
		return;
	}

	/* First run after this guard started working: nothing has ever written
	"active-inspection", so we cannot tell a genuinely new inspection from one already
	in progress. Adopt the current activity rather than wiping — a stale carry-over is
	recoverable, a destroyed in-progress inspection is not. Subsequent launches take
	the normal path below. */
	if (!activeInspection) {
		sessionStorage.setItem("liveInitDone", "1");
		localStorage.setItem("active-inspection", currentActivityId);
		return;
	}

	// New inspection or first load — wipe previous data and record new activity
	try {
		// IMPORTANT: clear only Ionic Storage / localStorage, not sessionStorage
		await clearStorage();
	} finally {
		sessionStorage.setItem("liveInitDone", "1");
		localStorage.setItem("active-inspection", currentActivityId);
	}
}

async function clearStorage() {
	/*await clearStorageAlert.present();
	await clearStorageAlert.onDidDismiss();*/

	localStorage.clear();
	await store.clear();
	app.state.damageMarkers = {};

	// Only reset photoData on FIRST live launch
	//if (!alreadyLaunched) {
	app.state.photoData = {
		"car-front": { src: "assets/img/car-front.png", updated: false },
		"car-driver-side": { src: "assets/img/car-driver-side.png", updated: false },
		"car-passenger-side": { src: "assets/img/car-passenger-side.png", updated: false },
		"car-back": { src: "assets/img/car-back.png", updated: false },
		"car-interior-front": { src: "assets/img/car-interior-front.png", updated: false },
		"car-interior-rear": { src: "assets/img/car-interior-rear.png", updated: false },
		"car-vin": { src: "assets/img/car-vin-label.png", updated: false },
		"car-top": { src: "assets/img/car-top.png", updated: false },
		"car-driver-profile": { src: "assets/img/car-driver-profile.png", updated: false },
		"car-passenger-profile": { src: "assets/img/car-passenger-profile.png", updated: false },
		"car-trunk": { src: "assets/img/car-trunk.png", updated: false },
		"car-odometer": { src: "assets/img/car-odometer.png", updated: false },
	};
	//}
	app.validator.resetAll();

	/* vehicle-data is otherwise only written once, at app startup, so without this the PDF
	throws on combinedVehicleDetails.vehicleInfo for anything generated before the next reload.
	app.data.dataTables lives in memory, so it survives the clear. */
	if (app.data?.dataTables) await storeTableData(app.data.dataTables);
}

async function buildDamageIssues(page2Data) {
	const out = [];
	const issues = Object.values(page2Data || {}).filter((it) => it && it.id && !it.id.endsWith("-text") && it.value === "Issue");
	consoleMsg(`Damage issues: ${issues}`);

	// Get all photo records once
	const keys = await store.keys();
	const photoKeys = keys.filter((k) => new RegExp(`^${app.state.storageKey}_photo\\d+-photoRow\\d+$`).test(k));
	const allPhotos = await Promise.all(photoKeys.map((k) => store.get(k)));

	for (const item of issues) {
		const question = item.string || "";
		const descObj = Object.values(page2Data).find((v) => v && v.id === `${item.id}-text`);
		const description = descObj?.value || "";
		const questionId = item.id;

		// Find photos matching this questionId
		const matchingPhotos = allPhotos.filter((p) => p && p.questionId === questionId);

		for (const photoObj of matchingPhotos) {
			if (photoObj.photo) out.push({ question, description, photo: photoObj.photo });
		}
	}

	consoleMsg("buildDamageIssues: damageIssues array built:", out);
	return out;
}

// IMPORTANT!!  KEEP THIS ONE PHOTO RESORATION FUNCTION OUTSIDE OF PHOTO CLASSES.  NEEDS TO BE CALLED EARLY
// IN APP INITIALIZATION.
async function restorePhotoData() {
	const stored = await store.get("photoData");
	if (stored && typeof stored === "object") {
		for (const [key, val] of Object.entries(stored)) {
			if (app.state.photoData[key]) {
				app.state.photoData[key].updated = val.updated;
			}
		}
	}
}
